import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import {
  Badge,
  Button,
  Callout,
  Card,
  Field,
  Input,
  Loading,
  PageHeader,
  Select,
  Table,
  Td,
} from '../components/ui/primitives';
import { gradeColour, relative } from '../lib/format';
import type { ClientRecord, DomainRecord } from '../lib/types';

export function DomainsPage() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    domain: '',
    exclusivity: 'shared',
    ownerClientId: '',
    espAccountId: '',
    trackingDomain: '',
    warmupProfile: 'conservative',
  });

  const { data, isLoading } = useQuery({
    queryKey: ['domains'],
    queryFn: () => api.get<{ domains: DomainRecord[] }>('/api/domains'),
  });
  const { data: esp } = useQuery({
    queryKey: ['esp-accounts'],
    queryFn: () => api.get<{ accounts: Array<{ id: string; name: string; provider: string }> }>('/api/esp/accounts'),
  });
  const { data: clients } = useQuery({
    queryKey: ['clients'],
    queryFn: () => api.get<{ clients: ClientRecord[] }>('/api/clients'),
  });

  const create = useMutation({
    mutationFn: () =>
      api.post('/api/domains', {
        domain: form.domain,
        exclusivity: form.exclusivity,
        ownerClientId: form.exclusivity === 'exclusive' ? form.ownerClientId : null,
        espAccountId: form.espAccountId,
        trackingDomain: form.trackingDomain || 'links.' + form.domain,
        warmupProfile: form.warmupProfile,
      }),
    onSuccess: () => {
      setOpen(false);
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['domains'] });
    },
    onError: (err: Error) => setError(err.message),
  });

  const scan = useMutation({
    mutationFn: () => api.post('/api/reputation/scan'),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['domains'] }),
  });

  if (isLoading) return <Loading />;

  return (
    <>
      <PageHeader
        title="Domains"
        description="Shared domains carry several clients and get stricter caps; an exclusive domain belongs to one client and can be pushed harder. Reputation is scored per domain and gates sending automatically."
        actions={
          <>
            <Button loading={scan.isPending} onClick={() => scan.mutate()}>
              Run reputation scan
            </Button>
            <Button variant="primary" onClick={() => setOpen((v) => !v)}>
              {open ? 'Cancel' : 'Add domain'}
            </Button>
          </>
        }
      />

      {open && (
        <Card title="New sending domain" className="mb-6">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Field label="Domain">
              <Input
                placeholder="outreach.example.com"
                value={form.domain}
                onChange={(e) => setForm({ ...form, domain: e.target.value })}
              />
            </Field>
            <Field label="Exclusivity">
              <Select
                value={form.exclusivity}
                onChange={(e) => setForm({ ...form, exclusivity: e.target.value })}
              >
                <option value="shared">Shared across clients</option>
                <option value="exclusive">Exclusive to one client</option>
              </Select>
            </Field>
            {form.exclusivity === 'exclusive' && (
              <Field label="Owning client">
                <Select
                  value={form.ownerClientId}
                  onChange={(e) => setForm({ ...form, ownerClientId: e.target.value })}
                >
                  <option value="">Select a client</option>
                  {(clients?.clients ?? []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Select>
              </Field>
            )}
            <Field label="ESP account">
              <Select
                value={form.espAccountId}
                onChange={(e) => setForm({ ...form, espAccountId: e.target.value })}
              >
                <option value="">Select a provider account</option>
                {(esp?.accounts ?? []).map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name} ({a.provider})
                  </option>
                ))}
              </Select>
            </Field>
            <Field
              label="Tracking domain"
              hint="CNAME this at the API host. Aligned tracking links are worth real inbox placement."
            >
              <Input
                placeholder={'links.' + (form.domain || 'example.com')}
                value={form.trackingDomain}
                onChange={(e) => setForm({ ...form, trackingDomain: e.target.value })}
              />
            </Field>
            <Field label="Warmup profile" hint="Conservative is correct for cold B2B traffic.">
              <Select
                value={form.warmupProfile}
                onChange={(e) => setForm({ ...form, warmupProfile: e.target.value })}
              >
                <option value="conservative">Conservative (30 days)</option>
                <option value="standard">Standard (21 days)</option>
                <option value="aggressive">Aggressive (10 days)</option>
              </Select>
            </Field>
          </div>
          {error && (
            <div className="mt-4">
              <Callout tone="bad">{error}</Callout>
            </div>
          )}
          <div className="mt-4">
            <Button variant="primary" loading={create.isPending} onClick={() => create.mutate()}>
              Add domain and verify DNS
            </Button>
          </div>
        </Card>
      )}

      <Card>
        <Table
          head={['Domain', 'Type', 'Status', 'Reputation', 'DNS', 'Warmup', 'Mailboxes', 'Blocklists', 'Clients']}
        >
          {(data?.domains ?? []).map((domain) => (
            <tr key={domain.id}>
              <Td>
                <Link to={'/domains/' + domain.id} className="font-mono text-xs font-medium text-ink-900 underline">
                  {domain.domain}
                </Link>
                {domain.trackingDomain && (
                  <div className="mt-0.5 font-mono text-[11px] text-ink-400">{domain.trackingDomain}</div>
                )}
              </Td>
              <Td>
                <Badge tone={domain.exclusivity === 'exclusive' ? 'info' : 'neutral'}>{domain.exclusivity}</Badge>
              </Td>
              <Td>
                <Badge
                  tone={
                    domain.status === 'active'
                      ? 'good'
                      : domain.status === 'quarantined'
                        ? 'bad'
                        : domain.status === 'warming'
                          ? 'info'
                          : 'warn'
                  }
                >
                  {domain.status.replace(/_/g, ' ')}
                </Badge>
              </Td>
              <Td>
                <span
                  className={
                    'rounded-md border px-2 py-0.5 text-xs font-semibold tabular-nums ' +
                    gradeColour(domain.reputation?.grade ?? null)
                  }
                >
                  {domain.reputation ? domain.reputation.score.toFixed(0) + ' ' + domain.reputation.grade : 'not scanned'}
                </span>
                {domain.reputation && (
                  <div className="mt-0.5 text-[11px] text-ink-400">{relative(domain.reputation.capturedAt)}</div>
                )}
              </Td>
              <Td>
                {domain.dns ? (
                  <Badge tone={domain.dns.score >= 85 ? 'good' : domain.dns.score >= 60 ? 'warn' : 'bad'}>
                    {domain.dns.score}/100
                  </Badge>
                ) : (
                  <span className="text-xs text-ink-400">--</span>
                )}
              </Td>
              <Td>
                {domain.warmup ? (
                  <div className="w-24">
                    <div className="h-1.5 w-full rounded-full bg-ink-200">
                      <div
                        className="h-1.5 rounded-full bg-ink-900"
                        style={{ width: domain.warmup.pct + '%' }}
                      />
                    </div>
                    <div className="mt-1 text-[11px] text-ink-500">
                      day {domain.warmup.day}/{domain.warmup.totalDays}
                    </div>
                  </div>
                ) : (
                  <span className="text-xs text-ink-400">--</span>
                )}
              </Td>
              <Td className="tabular-nums">{domain.identityCount}</Td>
              <Td>
                {domain.activeListings.length ? (
                  <Badge tone="bad">{domain.activeListings.length} listed</Badge>
                ) : (
                  <Badge tone="good">clear</Badge>
                )}
              </Td>
              <Td className="text-xs text-ink-500">
                {domain.clients.length ? domain.clients.map((c) => c.name).join(', ') : '--'}
              </Td>
            </tr>
          ))}
        </Table>
      </Card>
    </>
  );
}
